import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Avatar, Button, IconButton, InputAdornment, List, ListItemButton, ListItemText, TextField, CircularProgress, Badge } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { ReactComponent as SearchIcon } from '../../../../Assets/img/icons/search-icon-figma.svg';
import { useProfileContext } from "../../../ProfileDetailContext";
import { useQuantityContext } from '../../../QuantityContext';
import axios from 'axios';
import useApi from '../../../Utilities/useAPI';
import CryptoJS from 'crypto-js';
import UserDetailContext, { useUserDetailContext } from '../../../UserDetailContext';
import { useSelector, useDispatch } from 'react-redux';
import { getGlobleUsers } from "../../../../Redux/Reducers/ContactReducer";
import debounce from "lodash/debounce";

const AddRecommendation = ({ setaddRecommendation, setOpenAddMoreSection }) => {
    const { userDetails } = useUserDetailContext();
    const dispatch = useDispatch();


    const { connectionList, setConnectionList, addSection, setAddSection } = useProfileContext();
    const globalusersData = useSelector(state => state.ContactArray?.globalusersData?.data) || [];
    const [isSearching, setIsSearching] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');


    let limit = 20;
    const [step, setStep] = useState(1);
    const [searchRecommender, setSearchRecommender] = useState('');
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);

    const objectId = localStorage.getItem('objectId')?.trim();
    const apiKey = process.env.REACT_APP_API_KEY
    const { openSnackbar, handleSuccess, handleFailure } = useQuantityContext();
    const headers = {
        'x-api-key': apiKey,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${objectId}`
    };

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce(async (searchValue) => {
            const params = {
                searchParam: searchValue.trim() === "" ? "a" : searchValue,
                limit: 20,
                offsetValue: 1,
            };
            await dispatch(getGlobleUsers(params));
            setIsSearching(false);
        }, 500),
        [dispatch]
    );

    // Clean up debounce on unmount
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchRecommender(value);
        setSelectedContact(null);
        setIsSearching(true);
        debouncedSearch(value);
    };

    // Transform globalusersData into the format needed for contacts
    const transformedContacts = useMemo(() =>
        globalusersData.map(user => ({
            id: user.id,
            profilePic: user.profile_url,
            contactRedId: user.reference_id,
            encryptedContactRefId: CryptoJS.AES.encrypt(
                user.reference_id?.toString() || user.id.toString(),
                'UserDetailsSecretKey'
            ).toString(),
            contactName: `${user.first_name} ${user.last_name}`,
            contactRole: user.relationship_status,
            isVerified: user.is_verified,
            isFavourite: false,
            isActive: true,
            role: user.designation || user.field_of_study || user.title || '---',
            subTitle: user.company || user.institute || user.nickname || '---',
        })), [globalusersData]  // Only recomputes when globalusersData actually changes
    );

    const [hasMore, setHasMore] = useState(true);
    const { callApiFunction } = useApi();

    const fetchConnections = useCallback(async () => {
        try {
            const response = await callApiFunction('ConnectedContacts', {
                limit: limit,
                offset: connectionList.length
            });

            if (response?.statusCode === 200) {
                const newConnections = response.data.map(member => ({
                    id: member.id,
                    profilePic: member.profile_url,
                    contactRedId: member.contact_ref_id,
                    encryptedContactRefId: CryptoJS.AES.encrypt(
                        member.contact_ref_id.toString(),
                        'UserDetailsSecretKey'
                    ).toString(),
                    contactName: `${member.first_name} ${member.last_name}`,
                    contactRole: member.relationship_type,
                    isVerified: member.is_verified,
                    isFavourite: member?.is_favourite,
                    isActive: true,
                    role: member.designation || member.field_of_study || member.title || '---',
                    subTitle: member.company || member.institute || member.nickname || '---',
                }));

                setConnectionList(prev => [...prev, ...newConnections]);
                setAddSection((prevState) => ({
                    ...prevState,
                    professionalRecommendation: true
                }));

                if (newConnections.length < limit) {
                    setHasMore(false);
                }
            }
        } catch (err) {
            console.error('Error fetching connections:', err);
        }
    }, [limit, setConnectionList, setAddSection]);

    useEffect(() => {
        if (connectionList.length === 0) {
            fetchConnections();
        }
    }, [fetchConnections, connectionList.length])



    const [requestText, setRequestText] = useState(`Hi
I hope you're doing well. I wanted to ask if you would be willing to write a letter of recommendation for me.
I truly appreciated the time we worked together and believe your perspective would be valuable in supporting my application.
and I'd be happy to share any details or documents you might need.
Please let me know if you're comfortable with this. I sincerely appreciate your support.
Best regards, 
${userDetails?.firstName} ${userDetails?.lastName}
`);

    // Filter contacts based on the search query - using transformedContacts
    useEffect(() => {
        if (searchRecommender && !selectedContact) {
            const results = transformedContacts.filter(contact =>
                contact.contactName.toLowerCase().includes(searchRecommender.toLowerCase())
            );
            setFilteredContacts(results);
        } else if (!searchRecommender) {
            setFilteredContacts([]);  // Clear when input is empty
        }
    }, [searchRecommender, selectedContact, transformedContacts]);

    // Select a contact
    const handleContactSelect = (contact) => {
        setSelectedContact(contact);
        setSearchRecommender(contact.contactName);
        setFilteredContacts([]);
    };

    // Proceed to the next step
    const handleStepChange = () => {
        setStep(2);
        setSearchRecommender('');
    };

    // Complete the recommendation flow
    const handleNextClick = () => {
        setaddRecommendation(false);
        setOpenAddMoreSection(false);
    };

    const handleSubmitRecommendation = async () => {
        if (!selectedContact || !requestText) return;

        const postData = {
            contactRefId: selectedContact.contactRedId,
            text: requestText,
        };

        try {
            const response = await axios.post(
                "/mail/send-recommendation-request",
                postData,
                {
                    headers: headers,
                }
            );

            if (response.status == 200) {
                handleSuccess("Recommendation request sent successfully!");
            } else {
                handleFailure("Failed to send the recommendation request.");
            }
        } catch (error) {
            console.error("Error sending recommendation request:", error);
            handleFailure("Failed to send the recommendation request.");
        } finally {
            setaddRecommendation(false);
            setOpenAddMoreSection(false);
        }
    };

    // Custom Avatar with verification badge
    const VerifiedAvatar = ({ contact }) => {
        const isVerified = contact.isVerified === 1;

        return (
            <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                    isVerified ? (
                        <CheckCircleIcon
                            sx={{
                                fontSize: 20,
                                color: '#4caf50',
                                backgroundColor: 'white',
                                borderRadius: '50%'
                            }}
                        />
                    ) : (
                        <CancelIcon
                            sx={{
                                fontSize: 20,
                                color: '#f44336',
                                backgroundColor: 'white',
                                borderRadius: '50%'
                            }}
                        />
                    )
                }
            >
                <Avatar
                    alt={contact.contactName}
                    src={contact.profilePic}
                    sx={{ height: 40, width: 40 }}
                />
            </Badge>
        );
    };

    return (
        <>
            <div className="profile-modal-title-content p-4">
                <IconButton
                    className="modal-close-button"
                    onClick={() => {
                        setaddRecommendation(false);
                        setOpenAddMoreSection(true);
                    }}
                >
                    <CloseRoundedIcon />
                </IconButton>
                <div className="profile-modal-heading1 text-left">Ask for a Recommendation</div>
            </div>
            <div className="px-4 py-3">
                {step === 1 && (
                    <>
                        <div className="mb-3 text-left">Search for People</div>
                        <TextField
                            value={searchRecommender}
                            onChange={handleSearchChange}
                            placeholder="Search for People"
                            variant="outlined"
                            size="small"
                            className="searchBox-addPeople"
                            InputProps={{
                                startAdornment: selectedContact ? (
                                    <InputAdornment position="start">
                                        <VerifiedAvatar contact={selectedContact} />
                                    </InputAdornment>
                                ) : (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: isSearching && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {filteredContacts.length > 0 && (
                            <List className="recommenders-list">
                                {filteredContacts.map((contact) => (
                                    <ListItemButton
                                        className="gap-2"
                                        key={contact.id}
                                        onClick={() => handleContactSelect(contact)}
                                    >
                                        <VerifiedAvatar contact={contact} />
                                        <ListItemText
                                            primary={
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span>{contact.contactName}</span>
                                                </div>
                                            }
                                            secondary={
                                                contact.designation && (
                                                    <span style={{ fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                                                        {contact.designation}
                                                    </span>
                                                )
                                            }
                                        />

                                    </ListItemButton>

                                ))}
                            </List>
                        )}
                        {!isSearching && searchRecommender && filteredContacts.length === 0 && (
                            <div className="text-center mt-3 text-muted">
                                No users found
                            </div>
                        )}
                        <div className="d-flex justify-content-end mt-3">
                            <Button
                                variant="contained"
                                sx={{ backgroundColor: '#0044C6' }}
                                disabled={!selectedContact}
                                onClick={handleStepChange}
                            >
                                Next
                            </Button>
                        </div>
                    </>
                )}
                {step === 2 && selectedContact && (
                    <>
                        <div className="mb-3 text-left">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                                <VerifiedAvatar contact={selectedContact} />
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <strong>{selectedContact.contactName}</strong>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        {selectedContact.role !== '---' && selectedContact.role}
                                        {selectedContact.subTitle !== '---' && ` • ${selectedContact.subTitle}`}
                                    </div>
                                </div>
                            </div>
                            Provide details for your request
                        </div>
                        <div className="about-me-edit-container mt-3">
                            <textarea
                                value={requestText}
                                onChange={(e) => setRequestText(e.target.value)}
                                className="About-me-edit"
                                maxLength={500}
                            />
                            <div className="about-me-edit-count">{requestText.length}/500</div>
                        </div>
                        <div className="d-flex justify-content-end mt-3 gap-3">
                            <Button
                                sx={{ color: '#0044C6' }}
                                onClick={() => {
                                    setStep(1);
                                    setSelectedContact(null);
                                }}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                sx={{ backgroundColor: '#0044C6' }}
                                disabled={!requestText.trim()}
                                onClick={handleSubmitRecommendation}
                            >
                                Submit
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default AddRecommendation;